/**
 * Effects Director for Poker Reborn Effects Engine
 *
 * The central orchestrator that manages all effects in the system.
 * Subscribes to game events and dispatches appropriate effects.
 *
 * @see tasks/active/EFFECTS_ENGINE_IMPLEMENTATION_PLAN.md - Phase 2.4
 */

import { Container } from 'pixi.js';
import { log } from '@/utils/log';
import type { Effect } from '../core/Effect';
import type { EffectCue } from '../core/EffectCue';
import type { EffectResult, EffectsConfig } from '../core/EffectTypes';
import {
  EffectState,
  EffectPriority,
  DEFAULT_EFFECTS_CONFIG,
} from '../core/EffectTypes';
import { SequenceEffect } from '../core/SequenceEffect';
import { ParallelEffect } from '../core/ParallelEffect';
import { PriorityQueue } from '../utils/PriorityQueue';
import type { EffectFactory } from './EffectFactory';

// =============================================================================
// TYPES
// =============================================================================

/**
 * Layer registry for effect injection.
 * Maps layer names to PixiJS containers.
 */
export interface LayerRegistry {
  /** Background layer (Z-Index 0) */
  background?: Container;
  /** Table layer (Z-Index 1) */
  table?: Container;
  /** Seats layer (Z-Index 2) */
  seats?: Container;
  /** Cards layer (Z-Index 3) */
  cards?: Container;
  /** Particle layer (Z-Index 4) - confetti, coins, sparkles */
  particles?: Container;
  /** UI layer (Z-Index 5) */
  ui?: Container;
  /** Overlay effects layer (Z-Index 6) - screen flash, vignette */
  overlays?: Container;
  /** Popup layer (Z-Index 7) */
  popups?: Container;
  /** Flying cards container (inside cards layer) */
  flyingCards?: Container;
}

/**
 * Entry for an active effect being tracked.
 */
interface ActiveEffect {
  effect: Effect;
  startTime: number;
  promise: Promise<EffectResult>;
}

/**
 * Statistics about effects.
 */
export interface EffectsStats {
  /** Total effects played this session */
  totalPlayed: number;
  /** Currently active effects */
  activeCount: number;
  /** Effects in queue */
  queuedCount: number;
  /** Effects completed successfully */
  completedCount: number;
  /** Effects cancelled */
  cancelledCount: number;
  /** Effects skipped (low priority) */
  skippedCount: number;
  /** Average effect duration (ms) */
  averageDuration: number;
}

// =============================================================================
// EFFECTS DIRECTOR CLASS (P2.4.1-P2.4.14)
// =============================================================================

/**
 * The Effects Director - central orchestrator for all game effects.
 *
 * Responsibilities:
 * - Receive effect cues from event handlers
 * - Queue effects by priority
 * - Manage concurrent effect limits
 * - Track active effects
 * - Handle effect cancellation
 * - Provide statistics
 *
 * This is a singleton - use EffectsDirector.getInstance()
 */
export class EffectsDirector {
  private static _instance: EffectsDirector | null = null;

  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------

  private _config: EffectsConfig;
  private _effectFactory: EffectFactory | null = null;
  private _effectQueue: PriorityQueue<EffectCue>;
  private _activeEffects: Map<string, ActiveEffect>;
  private _isProcessing = false;
  private _enabled = true;
  private _layers: LayerRegistry = {};

  // Statistics
  private _stats: EffectsStats = {
    totalPlayed: 0,
    activeCount: 0,
    queuedCount: 0,
    completedCount: 0,
    cancelledCount: 0,
    skippedCount: 0,
    averageDuration: 0,
  };
  private _totalDuration = 0;

  // ---------------------------------------------------------------------------
  // Constructor & Singleton
  // ---------------------------------------------------------------------------

  private constructor(config: Partial<EffectsConfig> = {}) {
    this._config = { ...DEFAULT_EFFECTS_CONFIG, ...config };
    this._effectQueue = new PriorityQueue<EffectCue>();
    this._activeEffects = new Map();
  }

  /**
   * Get the singleton instance.
   */
  static getInstance(): EffectsDirector {
    if (!EffectsDirector._instance) {
      EffectsDirector._instance = new EffectsDirector();
    }
    return EffectsDirector._instance;
  }

  /**
   * Reset the singleton instance (for testing).
   */
  static resetInstance(): void {
    if (EffectsDirector._instance) {
      EffectsDirector._instance.cancelAll();
      EffectsDirector._instance._effectQueue.clear();
    }
    EffectsDirector._instance = null;
  }

  // ---------------------------------------------------------------------------
  // Configuration
  // ---------------------------------------------------------------------------

  /**
   * Update configuration.
   */
  configure(config: Partial<EffectsConfig>): void {
    this._config = { ...this._config, ...config };
    this._enabled = this._config.enabled;

    if (this._config.debugLogging) {
      log.effects.debug`EffectsDirector configured: ${JSON.stringify(this._config)}`;
    }
  }

  /**
   * Set the effect factory.
   */
  setEffectFactory(factory: EffectFactory): void {
    this._effectFactory = factory;
  }

  // ---------------------------------------------------------------------------
  // Layer Management
  // ---------------------------------------------------------------------------

  /**
   * Set the layer registry.
   * Call this after TableScreen is created to provide layer references.
   */
  setLayers(layers: LayerRegistry): void {
    this._layers = { ...this._layers, ...layers };

    if (this._config.debugLogging) {
      const layerNames = Object.keys(layers).filter(
        (k) => layers[k as keyof LayerRegistry] !== undefined
      );
      log.effects.debug`EffectsDirector: layers set: ${layerNames.join(', ')}`;
    }
  }

  /**
   * Get a specific layer.
   */
  getLayer<K extends keyof LayerRegistry>(name: K): LayerRegistry[K] {
    return this._layers[name];
  }

  /**
   * Get the particle layer (for confetti, coins, sparkles).
   */
  getParticleLayer(): Container | undefined {
    return this._layers.particles;
  }

  /**
   * Get the overlay effects layer (for screen flash, vignette).
   */
  getOverlayLayer(): Container | undefined {
    return this._layers.overlays;
  }

  /**
   * Get the flying cards container (for card deal animations).
   */
  getFlyingCardsContainer(): Container | undefined {
    return this._layers.flyingCards;
  }

  /**
   * Get all registered layers.
   */
  getLayers(): LayerRegistry {
    return { ...this._layers };
  }

  /**
   * Check if layers are registered.
   */
  hasLayers(): boolean {
    return Object.values(this._layers).some((l) => l !== undefined);
  }

  /**
   * Enable or disable effects globally.
   */
  setEnabled(enabled: boolean): void {
    this._enabled = enabled;
    this._config.enabled = enabled;

    if (!enabled) {
      this.cancelAll();
    }
  }

  /**
   * Check if effects are enabled.
   */
  get enabled(): boolean {
    return this._enabled;
  }

  /**
   * Get current statistics.
   */
  get stats(): EffectsStats {
    return {
      ...this._stats,
      activeCount: this._activeEffects.size,
      queuedCount: this._effectQueue.size,
    };
  }

  // ---------------------------------------------------------------------------
  // Play Methods (P2.4.2-P2.4.4)
  // ---------------------------------------------------------------------------

  /**
   * Play an effect (fire-and-forget).
   *
   * This is the primary API for triggering effects. The promise resolves
   * when the effect completes, but callers should NOT await it - effects
   * are fire-and-forget by design.
   *
   * @param cue The effect cue to play
   * @returns Promise that resolves when effect completes (don't await!)
   */
  play(cue: EffectCue): Promise<EffectResult> {
    if (!this._enabled) {
      return Promise.resolve({
        success: false,
        state: EffectState.CANCELLED,
        effectId: 'disabled',
        duration: 0,
        error: 'Effects disabled',
      });
    }

    // Check if should skip due to being busy
    if (this.shouldSkip(cue)) {
      this._stats.skippedCount++;
      return Promise.resolve({
        success: false,
        state: EffectState.CANCELLED,
        effectId: 'skipped',
        duration: 0,
        error: 'Skipped due to high load',
      });
    }

    // Queue the cue
    const priority = cue.priority ?? EffectPriority.NORMAL;
    this._effectQueue.enqueue(cue, priority);
    this._stats.queuedCount = this._effectQueue.size;

    // Process queue
    return this.processQueue(cue);
  }

  /**
   * Play multiple effects in sequence.
   */
  playSequence(cues: EffectCue[]): Promise<EffectResult> {
    if (!this._enabled || cues.length === 0) {
      return Promise.resolve({
        success: true,
        state: EffectState.COMPLETED,
        effectId: 'empty-sequence',
        duration: 0,
      });
    }

    const effects = cues
      .map((cue) => this.createEffect(cue))
      .filter((e): e is Effect => e !== null);

    if (effects.length === 0) {
      return Promise.resolve({
        success: false,
        state: EffectState.ERROR,
        effectId: 'sequence-error',
        duration: 0,
        error: 'No valid effects created',
      });
    }

    const sequence = new SequenceEffect(effects);
    return this.playEffect(sequence);
  }

  /**
   * Play multiple effects in parallel.
   */
  playParallel(cues: EffectCue[]): Promise<EffectResult> {
    if (!this._enabled || cues.length === 0) {
      return Promise.resolve({
        success: true,
        state: EffectState.COMPLETED,
        effectId: 'empty-parallel',
        duration: 0,
      });
    }

    const effects = cues
      .map((cue) => this.createEffect(cue))
      .filter((e): e is Effect => e !== null);

    if (effects.length === 0) {
      return Promise.resolve({
        success: false,
        state: EffectState.ERROR,
        effectId: 'parallel-error',
        duration: 0,
        error: 'No valid effects created',
      });
    }

    const parallel = new ParallelEffect(effects);
    return this.playEffect(parallel);
  }

  /**
   * Play an effect directly (bypasses queue).
   * Used internally and for pre-created effects.
   */
  playEffect(effect: Effect): Promise<EffectResult> {
    if (!this._enabled) {
      return Promise.resolve({
        success: false,
        state: EffectState.CANCELLED,
        effectId: effect.id,
        duration: 0,
        error: 'Effects disabled',
      });
    }

    // Check concurrent limit
    if (this._activeEffects.size >= this._config.maxConcurrentEffects) {
      // Cancel lowest priority effect if this one is higher
      if (!this.makeRoomFor(effect)) {
        this._stats.skippedCount++;
        return Promise.resolve({
          success: false,
          state: EffectState.CANCELLED,
          effectId: effect.id,
          duration: 0,
          error: 'Max concurrent effects reached',
        });
      }
    }

    // Cancel conflicting effects
    this.cancelConflicting(effect);

    // Track and play
    const startTime = performance.now();
    const promise = effect.play();

    this._activeEffects.set(effect.id, { effect, startTime, promise });
    this._stats.totalPlayed++;

    // Handle completion
    promise.then((result) => {
      this.onEffectComplete(effect.id, result);
    });

    return promise;
  }

  // ---------------------------------------------------------------------------
  // Cancellation Methods (P2.4.5-P2.4.7)
  // ---------------------------------------------------------------------------

  /**
   * Cancel all active effects.
   */
  cancelAll(): void {
    log.effects.debug`EffectsDirector: cancelling all ${this._activeEffects.size} active effects`;

    for (const [, entry] of this._activeEffects) {
      if (entry.effect.cancellable) {
        entry.effect.cancel();
        this._stats.cancelledCount++;
      }
    }

    this._activeEffects.clear();
    this._effectQueue.clear();
  }

  /**
   * Cancel effects with a specific tag.
   */
  cancelByTag(tag: string): number {
    let cancelled = 0;

    for (const [id, entry] of this._activeEffects) {
      if (entry.effect.hasTag(tag) && entry.effect.cancellable) {
        entry.effect.cancel();
        this._activeEffects.delete(id);
        this._stats.cancelledCount++;
        cancelled++;
      }
    }

    // Also remove from queue
    this._effectQueue.remove((cue) => cue.tags?.includes(tag) ?? false);

    log.effects.debug`EffectsDirector: cancelled ${cancelled} effects with tag "${tag}"`;
    return cancelled;
  }

  /**
   * Cancel effects targeting a specific target.
   */
  cancelByTarget(targetType: string, targetId?: string | number): number {
    let cancelled = 0;

    for (const [id, entry] of this._activeEffects) {
      const target = entry.effect.target;
      if (
        target.type === targetType &&
        (targetId === undefined || target.id === targetId) &&
        entry.effect.cancellable
      ) {
        entry.effect.cancel();
        this._activeEffects.delete(id);
        this._stats.cancelledCount++;
        cancelled++;
      }
    }

    // Also remove from queue
    this._effectQueue.remove((cue) =>
      cue.target.type === targetType &&
      (targetId === undefined || cue.target.id === targetId)
    );

    log.effects.debug`EffectsDirector: cancelled ${cancelled} effects on target ${targetType}:${targetId}`;
    return cancelled;
  }

  /**
   * Cancel a specific effect by ID.
   */
  cancelById(effectId: string): boolean {
    const entry = this._activeEffects.get(effectId);
    if (entry && entry.effect.cancellable) {
      entry.effect.cancel();
      this._activeEffects.delete(effectId);
      this._stats.cancelledCount++;
      return true;
    }
    return false;
  }

  // ---------------------------------------------------------------------------
  // Query Methods
  // ---------------------------------------------------------------------------

  /**
   * Get count of active effects.
   */
  get activeCount(): number {
    return this._activeEffects.size;
  }

  /**
   * Get count of queued effects.
   */
  get queuedCount(): number {
    return this._effectQueue.size;
  }

  /**
   * Check if system is busy (near capacity).
   */
  get isBusy(): boolean {
    const ratio = this._activeEffects.size / this._config.maxConcurrentEffects;
    return ratio >= this._config.busyThreshold;
  }

  /**
   * Get an active effect by ID.
   */
  getActiveEffect(effectId: string): Effect | undefined {
    return this._activeEffects.get(effectId)?.effect;
  }

  /**
   * Get all active effects.
   */
  getActiveEffects(): Effect[] {
    return Array.from(this._activeEffects.values()).map((e) => e.effect);
  }

  // ---------------------------------------------------------------------------
  // Private Methods
  // ---------------------------------------------------------------------------

  /**
   * Process the effect queue.
   */
  private async processQueue(originalCue: EffectCue): Promise<EffectResult> {
    // Find the cue we just added and create its effect
    const effect = this.createEffect(originalCue);
    if (!effect) {
      return {
        success: false,
        state: EffectState.ERROR,
        effectId: 'unknown',
        duration: 0,
        error: 'Failed to create effect',
      };
    }

    // Dequeue (we know it's there)
    this._effectQueue.dequeue();
    this._stats.queuedCount = this._effectQueue.size;

    return this.playEffect(effect);
  }

  /**
   * Create an effect from a cue.
   */
  private createEffect(cue: EffectCue): Effect | null {
    if (!this._effectFactory) {
      log.effects.warning`EffectsDirector: no effect factory set`;
      return null;
    }

    try {
      return this._effectFactory.createEffect(cue);
    } catch {
      // Only log as warning - missing creators are expected for unimplemented effect types
      log.effects.warning`EffectsDirector: no creator for ${cue.type}`;
      return null;
    }
  }

  /**
   * Check if an effect should be skipped due to high load.
   */
  private shouldSkip(cue: EffectCue): boolean {
    if (!this._config.skipLowPriorityWhenBusy) {
      return false;
    }

    const priority = cue.priority ?? EffectPriority.NORMAL;
    return this.isBusy && priority <= EffectPriority.LOW;
  }

  /**
   * Try to make room for a new effect by cancelling lower priority ones.
   * Returns true if room was made.
   */
  private makeRoomFor(effect: Effect): boolean {
    // Find lowest priority active effect
    let lowest: { id: string; priority: number } | null = null;

    for (const [id, entry] of this._activeEffects) {
      if (!entry.effect.cancellable) continue;

      if (!lowest || entry.effect.priority < lowest.priority) {
        lowest = { id, priority: entry.effect.priority };
      }
    }

    // If new effect has higher priority, cancel the lowest
    if (lowest && effect.priority > lowest.priority) {
      this.cancelById(lowest.id);
      return true;
    }

    return false;
  }

  /**
   * Cancel effects that conflict with the new effect.
   */
  private cancelConflicting(effect: Effect): void {
    for (const [id, entry] of this._activeEffects) {
      if (entry.effect.conflictsWith(effect) && entry.effect.cancellable) {
        log.effects.debug`EffectsDirector: cancelling conflicting effect ${id}`;
        entry.effect.cancel();
        this._activeEffects.delete(id);
        this._stats.cancelledCount++;
      }
    }
  }

  /**
   * Handle effect completion.
   */
  private onEffectComplete(effectId: string, result: EffectResult): void {
    const entry = this._activeEffects.get(effectId);
    if (entry) {
      this._activeEffects.delete(effectId);

      // Update stats
      if (result.success) {
        this._stats.completedCount++;
      } else if (result.state === EffectState.CANCELLED) {
        this._stats.cancelledCount++;
      }

      // Update average duration
      this._totalDuration += result.duration;
      this._stats.averageDuration = this._totalDuration / this._stats.completedCount;
    }
  }
}

// =============================================================================
// SINGLETON EXPORT
// =============================================================================

/**
 * Global effects director instance.
 */
export const effectsDirector = EffectsDirector.getInstance();
